import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MathJaxContext } from "better-react-mathjax";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button as UIButton } from "../../../components/ui";
import apiService from "../../../services/apiService";
import Loader from "../../../components/ui/Loader";
import { Option, Select } from "@material-tailwind/react";
import Question from "../question/Question.js";
import { useToast } from "../../../context/ToastContext";
import PDFViewer from "../../../components/PdfViewer";

const normalizeStatus = (status) => {
  if (status === "Ok") return "Ok";
  if (status === "Error") return "Error";
  return "Unseen";
};

const getQuestionId = (question) => {
  const value = question?.id || question?._id || "";
  return value ? String(value) : "";
};

const getRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.records)) return response.records;
  if (Array.isArray(response?.rows)) return response.rows;
  if (Array.isArray(response?.data?.records)) return response.data.records;
  if (Array.isArray(response?.data?.rows)) return response.data.rows;
  return [];
};
const IMAGE_FILE_NAME_REGEX =
  /(?:solution-)?image-[A-Za-z0-9._-]+\.(?:png|jpe?g|webp|gif)/i;

const getImageFileName = (value) => {
  const source = String(value || "")
    .trim()
    .replace(/&amp;/gi, "&")
    .replace(/\\+&/g, "&");

  if (!source) return "";

  const directMatch = source.match(IMAGE_FILE_NAME_REGEX);

  if (directMatch) {
    return directMatch[0];
  }

  try {
    const parsedUrl = new URL(source);
    const pathname = decodeURIComponent(parsedUrl.pathname || "");
    const basename = pathname.split("/").filter(Boolean).pop() || "";
    const fileMatch = basename.match(IMAGE_FILE_NAME_REGEX);

    return fileMatch ? fileMatch[0] : "";
  } catch (error) {
    return "";
  }
};

const normalizeImageReferences = (value) => {
  let text = String(value ?? "");

  if (!text.trim()) return "";

  /*
   * Markdown image containing full URL:
   * ![](https://.../image-123.png)
   * becomes:
   * image-123.png
   */
  text = text.replace(
    /!\[[^\]]*\]\s*\(\s*([^)]+)\s*\)/gi,
    (fullMatch, imageSource) => {
      const imageName = getImageFileName(imageSource);

      return imageName || fullMatch;
    },
  );

  /*
   * HTML image:
   * <img src="https://.../image-123.png">
   */
  text = text.replace(
    /<img\b[^>]*\bsrc=["']([^"']+)["'][^>]*>/gi,
    (fullMatch, imageSource) => {
      const imageName = getImageFileName(imageSource);

      return imageName || fullMatch;
    },
  );

  /*
   * Plain full image URL:
   * https://.../image-123.png
   *
   * Normal non-image URL ko change nahi karega.
   */
  text = text.replace(/https?:\/\/[^\s<>"')]+/gi, (fullUrl) => {
    const imageName = getImageFileName(fullUrl);

    return imageName || fullUrl;
  });

  return text
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
};

const normalizeOptionArray = (options) => {
  if (!Array.isArray(options)) return [];

  return options
    .map((option) =>
      collapseDuplicateOptionLabel(normalizeImageReferences(option)),
    )
    .filter((option) => String(option || "").trim());
};

const normalizeQuestionForCompare = (question) => {
  if (!question || typeof question !== "object") {
    return question;
  }

  return {
    ...question,

    question_eng: normalizeImageReferences(question?.question_eng),

    question_hin: normalizeImageReferences(question?.question_hin),

    question_later_eng: normalizeImageReferences(question?.question_later_eng),

    question_later_hin: normalizeImageReferences(question?.question_later_hin),

    options_eng: normalizeOptionArray(question?.options_eng),

    options_hin: normalizeOptionArray(question?.options_hin),

    image: normalizeImageReferences(question?.image),

    image_eng: normalizeImageReferences(question?.image_eng),

    image_hin: normalizeImageReferences(question?.image_hin),

    status: normalizeStatus(question?.status),
  };
};

const PRINTED_CHOICE_LABEL_REGEX =
  /^\s*(?:\(\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*\)|\[\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*\]|(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\s*[\).,:;\-])\s*/i;

const collapseDuplicateOptionLabel = (value) => {
  const text = String(value || "").trim();
  const label =
    "(?:\\(\\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\\s*\\)|" +
    "\\[\\s*(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\\s*\\]|" +
    "(?:[1-9]|[A-Ha-h]|VIII|VII|VI|IV|III|II|I|viii|vii|vi|iv|iii|ii|i)\\s*[\\).,:;\\-])";
  const match = text.match(
    new RegExp(`^\\s*(${label})\\s+(${label})\\s+`, "i"),
  );

  if (!match) return text;

  const first = String(match[1] || "")
    .replace(/\s+/g, "")
    .toLowerCase();
  const second = String(match[2] || "")
    .replace(/\s+/g, "")
    .toLowerCase();

  return first === second
    ? `${match[1]} ${text.slice(match[0].length)}`.trim()
    : text;
};

function CompareQSet() {
  const navigate = useNavigate();
  const { id: qSetId } = useParams();
  const [searchParams] = useSearchParams();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState(searchParams.get("filter") || null);

  const { showSuccess, showError } = useToast();

  const mathJaxConfig = useMemo(
    () => ({
      loader: {
        load: ["input/tex", "output/chtml", "[tex]/ams"],
      },
      tex: {
        packages: {
          "[+]": ["ams"],
        },
        inlineMath: [
          ["$", "$"],
          ["\\(", "\\)"],
        ],
        displayMath: [
          ["$$", "$$"],
          ["\\[", "\\]"],
        ],
        processEscapes: true,
        processEnvironments: true,
      },
      chtml: {
        scale: 1,
        matchFontHeight: true,
      },
      options: {
        skipHtmlTags: [
          "script",
          "noscript",
          "style",
          "textarea",
          "pre",
          "code",
        ],
      },
    }),
    [],
  );

  const fetchQuestions = useCallback(
    async (showPageLoader = true) => {
      if (!qSetId) return [];

      const params = new URLSearchParams({
        qSet: String(qSetId),
        limit: "all",
        sortField: "question_num",
        sortOrder: "ASC",
      });

      if (showPageLoader) {
        setLoading(true);
      }

      try {
        const response = await apiService.request(
          `/i/ims/aiquestion?${params.toString()}`,
          {
            method: "GET",
          },
        );

        // const questionArray = getRows(response)
        //   .map((question) => ({
        //     ...question,
        //     status: normalizeStatus(question?.status),
        //   }))
        //   .sort(
        //     (a, b) =>
        //       Number(a?.question_num || 0) - Number(b?.question_num || 0),
        //   );
        const questionArray = getRows(response)
          .map(normalizeQuestionForCompare)
          .sort(
            (a, b) =>
              Number(a?.question_num || 0) - Number(b?.question_num || 0),
          );
        setQuestions(questionArray);
        return questionArray;
      } catch (err) {
        console.log("Error fetching questions:", err);
        setQuestions([]);
        showError(
          err?.response?.data?.message ||
            err?.data?.message ||
            err?.message ||
            "Failed to fetch questions",
        );
        return [];
      } finally {
        if (showPageLoader) {
          setLoading(false);
        }
      }
    },
    [qSetId, showError],
  );

  useEffect(() => {
    fetchQuestions(true);
  }, [fetchQuestions]);

  const changeStatus = useCallback((questionId, status) => {
    const finalQuestionId = String(questionId || "");
    const finalStatus = normalizeStatus(status);

    if (!finalQuestionId) {
      console.warn("QUESTION_STATUS_CHANGE_SKIPPED_ID_MISSING:", {
        questionId,
        status,
      });
      return;
    }

    setQuestions((previousQuestions) =>
      previousQuestions.map((question) => {
        const currentId = getQuestionId(question);

        if (currentId !== finalQuestionId) {
          return question;
        }

        return {
          ...question,
          status: finalStatus,
        };
      }),
    );
  }, []);

  const saveStatus = useCallback(async () => {
    if (!qSetId) {
      showError("QSet id not found");
      return;
    }

    if (!Array.isArray(questions) || questions.length === 0) {
      showError("No questions found");
      return;
    }

    const updateArray = questions
      .map((question) => ({
        id: getQuestionId(question),
        qSet: String(qSetId),
        status: normalizeStatus(question?.status),
      }))
      .filter((item) => item.id);

    if (updateArray.length === 0) {
      showError("Question ids not found");
      return;
    }

    setSaving(true);

    try {
      const response = await apiService.request(
        "/i/ims/aiquestion/bulkupdate",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            updateArray,
          }),
        },
      );

      if (
        response?.code === "ERROR" ||
        response?.success === false ||
        response?.status === false
      ) {
        throw new Error(response?.message || "Status update failed");
      }

      await fetchQuestions(false);
      showSuccess("Status Saved!!");
    } catch (err) {
      console.log("AIQUESTION_STATUS_BULK_UPDATE_ERROR:", err);
      showError(
        err?.response?.data?.message ||
          err?.data?.message ||
          err?.message ||
          "Status update failed",
      );
    } finally {
      setSaving(false);
    }
  }, [questions, qSetId, fetchQuestions, showError, showSuccess]);

  const filteredQuestions = questions.filter((question) => {
    if (!filter) return true;
    return normalizeStatus(question?.status) === filter;
  });

  return (
    <MathJaxContext config={mathJaxConfig}>
      <div className="p-2">
        <div className="m-5 flex items-center justify-between">
          <h1 className="text-3xl font-bold">Compare Results</h1>

          <UIButton
            onClick={() => navigate("/tools/qSet")}
            variant="light"
            size="sm"
            icon={FaArrowLeft}
          >
            Back to List
          </UIButton>
        </div>

        <div className="m-5 flex items-start justify-between">
          <div className="flex items-start text-sm">
            <span className="text-gray-500">Home</span>
            <span className="mx-2">/</span>
            <span
              onClick={() => navigate("/tools/qSet")}
              className="cursor-pointer text-gray-500"
            >
              QSet
            </span>
            <span className="mx-2">/</span>
            <span className="text-blue-600">Compare</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="ml-2 w-60">
              <Select
                variant="standard"
                label="Select Filter"
                value={filter || "null"}
                onChange={(value) => {
                  setFilter(value === "null" ? null : value);
                }}
              >
                <Option value="Ok">Ok</Option>
                <Option value="Error">Error</Option>
                <Option value="null">None</Option>
              </Select>
            </div>

            <UIButton
              onClick={saveStatus}
              disabled={saving}
              variant="info"
              size="sm"
            >
              {saving ? "Saving..." : "Save"}
            </UIButton>
          </div>
        </div>

        <div className="flex flex-col rounded-md border bg-white p-2">
          <div className="flex">
            <div className="w-1/2">
              <PDFViewer qSetId={qSetId} />
            </div>

            <div className="h-screen w-1/2 overflow-y-auto">
              {loading ? (
                <Loader />
              ) : filteredQuestions.length > 0 ? (
                filteredQuestions.map((question, index) => (
                  <Question
                    key={
                      getQuestionId(question) ||
                      `question-${question?.question_num || index}`
                    }
                    question={question}
                    i={index}
                    qSetId={qSetId}
                    changeStatus={changeStatus}
                  />
                ))
              ) : (
                <div className="p-4 text-gray-500">No questions found</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MathJaxContext>
  );
}

export default CompareQSet;

// import React, { useCallback, useEffect, useState } from "react";
// import { MathJaxContext } from "better-react-mathjax";
// import { FaArrowLeft } from "react-icons/fa";
// import { useNavigate, useParams, useSearchParams } from "react-router-dom";
// import { Button as UIButton } from "../../../components/ui";
// import apiService from "../../../services/apiService";
// import Loader from "../../../components/ui/Loader";
// import { Option, Select } from "@material-tailwind/react";
// import Question from "../question/Question.js";
// import { useToast } from "../../../context/ToastContext";
// import PDFViewer from "../../../components/PdfViewer";

// const normalizeStatus = (status) => {
//   if (status === "Ok") return "Ok";
//   if (status === "Error") return "Error";
//   return "Unseen";
// };

// const getQuestionId = (question) => {
//   const value = question?.id || question?._id || "";
//   return value ? String(value) : "";
// };

// const getRows = (response) => {
//   if (Array.isArray(response)) return response;
//   if (Array.isArray(response?.data)) return response.data;
//   if (Array.isArray(response?.records)) return response.records;
//   if (Array.isArray(response?.rows)) return response.rows;
//   if (Array.isArray(response?.data?.records)) return response.data.records;
//   if (Array.isArray(response?.data?.rows)) return response.data.rows;
//   return [];
// };

// function CompareQSet() {
//   const navigate = useNavigate();
//   const { id: qSetId } = useParams();
//   const [searchParams] = useSearchParams();

//   const [questions, setQuestions] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [saving, setSaving] = useState(false);
//   const [filter, setFilter] = useState(searchParams.get("filter") || null);

//   const { showSuccess, showError } = useToast();

//   const config = {
//     tex: {
//       inlineMath: [
//         ["$", "$"],
//         ["\\(", "\\)"],
//       ],
//       displayMath: [
//         ["$$", "$$"],
//         ["\\[", "\\]"],
//       ],
//     },
//     loader: { load: ["input/tex", "output/chtml"] },
//   };

//   const fetchQuestions = useCallback(
//     async (showPageLoader = true) => {
//       if (!qSetId) return [];

//       const params = new URLSearchParams({
//         qSet: String(qSetId),
//         limit: "all",
//         sortField: "question_num",
//         sortOrder: "ASC",
//       });

//       if (showPageLoader) {
//         setLoading(true);
//       }

//       try {
//         const response = await apiService.request(
//           `/i/ims/aiquestion?${params.toString()}`,
//           {
//             method: "GET",
//           },
//         );

//         const questionArray = getRows(response)
//           .map((question) => ({
//             ...question,
//             status: normalizeStatus(question?.status),
//           }))
//           .sort(
//             (a, b) =>
//               Number(a?.question_num || 0) - Number(b?.question_num || 0),
//           );

//         setQuestions(questionArray);
//         return questionArray;
//       } catch (err) {
//         console.log("Error fetching questions:", err);
//         setQuestions([]);
//         showError(
//           err?.response?.data?.message ||
//             err?.data?.message ||
//             err?.message ||
//             "Failed to fetch questions",
//         );
//         return [];
//       } finally {
//         if (showPageLoader) {
//           setLoading(false);
//         }
//       }
//     },
//     [qSetId, showError],
//   );

//   useEffect(() => {
//     fetchQuestions(true);
//   }, [fetchQuestions]);

//   const changeStatus = useCallback((questionId, status) => {
//     const finalQuestionId = String(questionId || "");
//     const finalStatus = normalizeStatus(status);

//     if (!finalQuestionId) {
//       console.warn("QUESTION_STATUS_CHANGE_SKIPPED_ID_MISSING:", {
//         questionId,
//         status,
//       });
//       return;
//     }

//     setQuestions((prevQuestions) =>
//       prevQuestions.map((question) => {
//         const currentId = getQuestionId(question);

//         if (currentId !== finalQuestionId) {
//           return question;
//         }

//         return {
//           ...question,
//           status: finalStatus,
//         };
//       }),
//     );
//   }, []);

//   const saveStatus = useCallback(async () => {
//     if (!qSetId) {
//       showError("QSet id not found");
//       return;
//     }

//     if (!Array.isArray(questions) || questions.length === 0) {
//       showError("No questions found");
//       return;
//     }

//     const updateArray = questions
//       .map((question) => ({
//         id: getQuestionId(question),
//         qSet: String(qSetId),
//         status: normalizeStatus(question?.status),
//       }))
//       .filter((item) => item.id);

//     if (updateArray.length === 0) {
//       showError("Question ids not found");
//       return;
//     }

//     console.log("AIQUESTION_STATUS_BULK_UPDATE_PAYLOAD:", {
//       updateArray,
//     });

//     setSaving(true);

//     try {
//       const response = await apiService.request(
//         "/i/ims/aiquestion/bulkupdate",
//         {
//           method: "PUT",
//           headers: {
//             "Content-Type": "application/json",
//           },
//           body: JSON.stringify({
//             updateArray,
//           }),
//         },
//       );

//       console.log("AIQUESTION_STATUS_BULK_UPDATE_RESPONSE:", response);

//       if (
//         response?.code === "ERROR" ||
//         response?.success === false ||
//         response?.status === false
//       ) {
//         throw new Error(response?.message || "Status update failed");
//       }

//       /*
//        * Backend response mein updated rows aaye ya na aaye,
//        * DB se dobara fetch karke exact saved status dikhayenge.
//        */
//       await fetchQuestions(false);
//       showSuccess("Status Saved!!");
//     } catch (err) {
//       console.log("AIQUESTION_STATUS_BULK_UPDATE_ERROR:", err);
//       showError(
//         err?.response?.data?.message ||
//           err?.data?.message ||
//           err?.message ||
//           "Status update failed",
//       );
//     } finally {
//       setSaving(false);
//     }
//   }, [questions, qSetId, fetchQuestions, showError, showSuccess]);

//   const filteredQuestions = questions.filter((question) => {
//     if (!filter) return true;
//     return normalizeStatus(question?.status) === filter;
//   });

//   return (
//     <MathJaxContext config={config}>
//       <div className="p-2">
//         <div className="m-5 flex items-center justify-between">
//           <h1 className="text-3xl font-bold">Compare Results</h1>

//           <UIButton
//             onClick={() => navigate("/tools/qSet")}
//             variant="light"
//             size="sm"
//             icon={FaArrowLeft}
//           >
//             Back to List
//           </UIButton>
//         </div>

//         <div className="m-5 flex items-start justify-between">
//           <div className="flex items-start text-sm">
//             <span className="text-gray-500">Home</span>
//             <span className="mx-2">/</span>
//             <span
//               onClick={() => navigate("/tools/qSet")}
//               className="cursor-pointer text-gray-500"
//             >
//               QSet
//             </span>
//             <span className="mx-2">/</span>
//             <span className="text-blue-600">Compare</span>
//           </div>

//           <div className="flex items-center gap-2">
//             <div className="ml-2 w-60">
//               <Select
//                 variant="standard"
//                 label="Select Filter"
//                 value={filter || "null"}
//                 onChange={(value) => {
//                   setFilter(value === "null" ? null : value);
//                 }}
//               >
//                 <Option value="Ok">Ok</Option>
//                 <Option value="Error">Error</Option>
//                 <Option value="null">None</Option>
//               </Select>
//             </div>

//             <UIButton
//               onClick={saveStatus}
//               disabled={saving}
//               variant="info"
//               size="sm"
//             >
//               {saving ? "Saving..." : "Save"}
//             </UIButton>
//           </div>
//         </div>

//         <div className="flex flex-col rounded-md border bg-white p-2">
//           <div className="flex">
//             <div className="w-1/2">
//               <PDFViewer qSetId={qSetId} />
//             </div>

//             <div className="h-screen w-1/2 overflow-y-auto">
//               {loading ? (
//                 <Loader />
//               ) : filteredQuestions.length > 0 ? (
//                 filteredQuestions.map((question, index) => (
//                   <Question
//                     key={
//                       getQuestionId(question) ||
//                       `question-${question?.question_num || index}`
//                     }
//                     question={question}
//                     i={index}
//                     qSetId={qSetId}
//                     changeStatus={changeStatus}
//                   />
//                 ))
//               ) : (
//                 <div className="p-4 text-gray-500">No questions found</div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </MathJaxContext>
//   );
// }

// export default CompareQSet;
